import { useState, useEffect, useCallback, useRef } from "react";
import {
  Box, Button, Chip, CircularProgress,
  Collapse, Dialog, DialogActions, DialogContent,
  DialogContentText, DialogTitle,
  IconButton, InputAdornment,
  Stack, TextField, Typography,
  Table, TableBody, TableCell,
  TableHead, TableRow, Paper, Tooltip, Avatar,
  FormControl, Select, MenuItem, InputLabel, Divider,
  TableContainer,
} from "@mui/material";
import { parseProductListResponse } from "utils/parseListResponse";
import { useCurrency } from "contexts/CurrencyContext";
import { formatCurrency } from "utils/currency";

import AddIcon from "@mui/icons-material/Add";
import TableActionButton from "components/table/TableActionButton";
import TableActions from "components/table/TableActions";
import TableStatusChip from "components/table/TableStatusChip";
import { timeChipSx, vegIndicatorSx, vegIndicatorDotSx, highlightChipSx } from "components/table/tableChipStyles";
import SearchIcon from "@mui/icons-material/Search";
import ShopIcon from "@mui/icons-material/Storefront";
import CategoryIcon from "@mui/icons-material/Category";
import RestaurantMenuIcon from "@mui/icons-material/RestaurantMenu";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import RefreshIcon from "@mui/icons-material/Refresh";
import DeleteIcon from "@mui/icons-material/Delete";

import api, { IMG_BASE_URL } from "../../utils/api";
import MainCard from "components/MainCard";
import PageHeader from "components/PageHeader";
import ProductFilteredByStoreForm from "./ProductFilteredByStoreForm";
import { useToast } from "contexts/ToastContext";

const getImgUrl = (p) => {
  if (p?.image_url) return p.image_url;
  const d = p?.doc || p?.Doc;
  if (d?.imageUrl) return d.imageUrl;
  if (d?.attachment_url) return `${IMG_BASE_URL}/${d.attachment_url}`;
  return null;
};

function VariantRows({ variants }) {
  if (!variants?.length) return null;
  return (
    <TableRow>
      <TableCell colSpan={13} sx={{ py: 0, bgcolor: "grey.50", borderBottom: "2px solid", borderColor: "primary.lighter" }}>
        <Collapse in timeout="auto" unmountOnExit>
          <Box sx={{ px: 5, py: 1.5 }}>
            <Typography variant="caption" fontWeight={700} color="text.secondary"
              textTransform="uppercase" letterSpacing={0.5} mb={1} display="block">
              Variants ({variants.length})
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "grey.100" }}>
                  {["#", "Name", `Price (${currency.symbol})`, "Description", "Sequence"].map((h) => (
                    <TableCell key={h}
                      sx={{
                        fontWeight: 700, fontSize: "0.68rem", textTransform: "uppercase",
                        color: "text.secondary", py: 0.8, letterSpacing: 0.4
                      }}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {variants.map((v, i) => (
                  <TableRow key={v.id} hover>
                    <TableCell sx={{ py: 0.8, color: "text.disabled", width: 36 }}>{i + 1}</TableCell>
                    <TableCell sx={{ py: 0.8, fontWeight: 600 }}>{v.name}</TableCell>
                    <TableCell sx={{ py: 0.8, color: "primary.main", fontWeight: 700 }}>
                      {formatCurrency(v.price, currency.code, currency.symbol)}
                    </TableCell>
                    <TableCell sx={{ py: 0.8, color: "text.secondary", maxWidth: 260 }}>
                      {v.description || "—"}
                    </TableCell>
                    <TableCell sx={{ py: 0.8, color: "text.disabled" }}>{v.sequence ?? "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Collapse>
      </TableCell>
    </TableRow>
  );
}

function MaxCountCell({ limit }) {
  if (!limit) {
    return <Typography variant="caption" color="text.disabled">—</Typography>;
  }

  const { maximum_count, ordered_count, remaining_count, is_available } = limit;
  const isUnlimited = maximum_count === 0;

  if (isUnlimited) {
    return <Chip label="Unlimited" size="small" sx={timeChipSx} />;
  }

  const pct = Math.round((ordered_count / maximum_count) * 100);
  const color = !is_available ? "error" : pct >= 80 ? "warning" : "success";

  return (
    <Box sx={{ minWidth: 90 }}>
      <Stack direction="row" alignItems="center" justifyContent="center" gap={0.5} mb={0.4}>
        <Typography variant="caption" fontWeight={700} color={`${color}.main`}>
          {remaining_count ?? 0}
        </Typography>
        <Typography variant="caption" color="text.disabled">
          / {maximum_count}
        </Typography>
      </Stack>
      <Box sx={{ height: 4, borderRadius: 2, bgcolor: "grey.200", overflow: "hidden" }}>
        <Box sx={{
          height: "100%", borderRadius: 2,
          width: `${Math.min(pct, 100)}%`,
          bgcolor: !is_available ? "error.main" : pct >= 80 ? "warning.main" : "success.main",
          transition: "width 0.3s",
        }} />
      </Box>
      {!is_available && (
        <Typography variant="caption" color="error.main"
          display="block" textAlign="center" mt={0.3}>
          Sold out
        </Typography>
      )}
    </Box>
  );
}

function ProductRow({ p, idx, onEdit, onDelete, onToggleStatus, togglingId }) {
  const { currency } = useCurrency();
  const [expanded, setExpanded] = useState(false);
  const hasVariants = p.ProductVariants?.length > 0;
  const image = getImgUrl(p);

  return (
    <>
      <TableRow hover sx={{
        verticalAlign: "middle",
        "& > *": { borderBottom: expanded ? "unset" : undefined }
      }}>

        {/* expand toggle */}
        <TableCell sx={{ width: 44, py: 1, px: 1 }}>
          {hasVariants && (
            <IconButton size="small" onClick={() => setExpanded(!expanded)}>
              {expanded
                ? <KeyboardArrowUpIcon fontSize="small" />
                : <KeyboardArrowDownIcon fontSize="small" />}
            </IconButton>
          )}
        </TableCell>

        {/* # */}
        <TableCell sx={{ color: "text.disabled", fontSize: 13, width: 44 }}>
          {idx + 1}
        </TableCell>

        {/* image */}
        <TableCell sx={{ width: 64 }}>
          <Avatar src={image || undefined} variant="rounded"
            sx={{
              width: 48, height: 48, bgcolor: "primary.lighter",
              border: "1px solid", borderColor: "divider"
            }}>
            {!image && <RestaurantMenuIcon sx={{ fontSize: 20 }} />}
          </Avatar>
        </TableCell>

        {/* name + arabic */}
        <TableCell sx={{ minWidth: 180 }}>
          <Stack direction="row" alignItems="flex-start" gap={0.75}>
            {p.is_veg !== null && p.is_veg !== undefined && (
              <Tooltip title={p.is_veg ? "Veg" : "Non-Veg"}>
                <Box sx={vegIndicatorSx(p.is_veg)}>
                  <Box sx={vegIndicatorDotSx(p.is_veg)} />
                </Box>
              </Tooltip>
            )}
            <Box>
              <Typography fontWeight={600} variant="body2">{p.name}</Typography>
              {p.name_ar && (
                <Typography variant="caption" color="text.secondary"
                  display="block" sx={{ direction: "rtl" }}>
                  {p.name_ar}
                </Typography>
              )}
            </Box>
          </Stack>
        </TableCell>

        {/* description */}
        <TableCell sx={{ minWidth: 160 }}>
          <Typography variant="caption" color="text.secondary"
            sx={{
              display: "-webkit-box", WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical", overflow: "hidden", maxWidth: 200
            }}>
            {p.description || "—"}
          </Typography>
        </TableCell>

        {/* category */}
        <TableCell sx={{ minWidth: 130 }}>
          <Typography variant="body2" color="text.secondary">
            {p.ProductCategory?.name_en || "—"}
          </Typography>
        </TableCell>

        {/* price */}
        <TableCell align="right" sx={{ minWidth: 90 }}>
          <Typography fontWeight={700} color="primary.main" variant="body2">
            {formatCurrency(p.price, currency.code, currency.symbol)}
          </Typography>
        </TableCell>

        {/* variants count */}
        <TableCell align="center" sx={{ minWidth: 90 }}>
          {hasVariants ? (
            <Chip
              label={`${p.ProductVariants.length} variant${p.ProductVariants.length > 1 ? "s" : ""}`}
              size="small" color="secondary" variant="outlined"
              onClick={() => setExpanded(!expanded)}
              sx={{ cursor: "pointer" }}
            />
          ) : (
            <Typography variant="caption" color="text.disabled">—</Typography>
          )}
        </TableCell>

        {/* highlight */}
        <TableCell align="center" sx={{ minWidth: 90 }}>
          <Chip
            label={p.highlight ? "Yes" : "No"}
            size="small"
            sx={highlightChipSx(p.highlight)}
          />
        </TableCell>

        {/* sequence */}
        <TableCell align="center" sx={{ minWidth: 60 }}>
          <Typography variant="body2" color="text.secondary">{p.sequence ?? "—"}</Typography>
        </TableCell>

        {/*  max count */}
        <TableCell align="center" sx={{ minWidth: 110 }}>
          <MaxCountCell limit={p.limit} />
        </TableCell>

        {/* status toggle */}
        <TableCell align="center" sx={{ minWidth: 95 }}>
          <TableStatusChip
            active={!!p.status}
            loading={togglingId === p.id}
            onClick={() => togglingId !== p.id && onToggleStatus(p)}
            sx={{ minWidth: 90 }}
          />
        </TableCell>

        {/* actions */}
        <TableCell align="center" sx={{ minWidth: 90 }}>
          <TableActions>
            <TableActionButton action="edit" onClick={() => onEdit(p)} />
            <TableActionButton action="delete" onClick={() => onDelete(p)} />
          </TableActions>
        </TableCell>

      </TableRow>

      {expanded && <VariantRows variants={p.ProductVariants} />}
    </>
  );
}

const PAGE_LIMIT = 8;
const ALL_CATEGORIES = "__all__";

export default function ProductFilteredByStoreList() {
  const { showToast, showApiError } = useToast();
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [stores, setStores] = useState([]);
  const [selectedStore, setSelectedStore] = useState("");
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(ALL_CATEGORIES);
  const [storesReady, setStoresReady] = useState(false);
  const [search, setSearch] = useState("");
  const [searchDebounced, setSearchDebounced] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [hasFetched, setHasFetched] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  const nextPageRef = useRef(1);
  const scrollRef = useRef(null);
  const loadMoreSentinelRef = useRef(null);
  const loadMoreInFlightRef = useRef(false);
  const loadMoreRef = useRef(() => {});
  
  
  const [formModal, setFormModal] = useState({ open: false, id: null });
  const [delDialog, setDelDialog] = useState({ open: false, id: null, name: "" });
  const [deleting, setDeleting] = useState(false);

  const loadStores = useCallback(async () => {
    try {
      const res = await api.get("/store/list");
      const data = res.data?.data || [];
      const sorted = [...data].sort((a, b) => b.store_id - a.store_id);
      setStores(sorted);
      if (sorted.length > 0) {
        setSelectedStore(sorted[0].store_id);
      }
    } catch (err) {
      console.error("Failed to fetch stores:", err);
    } finally {
      setStoresReady(true);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setSearchDebounced(search.trim()), 400);
    return () => clearTimeout(timer);
  }, [search]);

  const loadCategories = useCallback(async () => {
    if (!selectedStore) {
      setCategories([]);
      setSelectedCategory(ALL_CATEGORIES);
      return;
    }
    try {
      const res = await api.get("/product/category/list", {
        params: { store_id: selectedStore }
      });
      setCategories(res.data?.data || []);
    } catch (err) {
      console.error("Failed to fetch categories:", err);
      setCategories([]);
    }
  }, [selectedStore]);

  useEffect(() => {
    setSelectedCategory(ALL_CATEGORIES);
    loadCategories();
  }, [loadCategories]);

  const buildListParams = useCallback(
    (pageNum) => {
      const params = { page: pageNum, limit: PAGE_LIMIT };
      if (selectedStore) params.store_id = selectedStore;
      if (selectedCategory && selectedCategory !== ALL_CATEGORIES) {
        params.category_id = selectedCategory;
      }
      if (searchDebounced) {
        params.search = searchDebounced;
        params.q = searchDebounced;
      }
      return params;
    },
    [selectedStore, selectedCategory, searchDebounced]
  );

  const fetchProductsPage = useCallback(
    async (pageNum) => {
      const res = await api.get("/product/list", { params: buildListParams(pageNum) });
      return parseProductListResponse(res);
    },
    [buildListParams]
  );

  const loadInitial = useCallback(async () => {
    if (!storesReady) return;

    setLoading(true);
    setHasMore(true);
    nextPageRef.current = 1;
    try {
      const result = await fetchProductsPage(1);
      setProducts(result.products);
      setTotal(result.total);
      setHasMore(result.currentPage < result.totalPages);
      nextPageRef.current = 2;
    } catch (err) {
      console.error("Failed to fetch products:", err);
      setProducts([]);
      setTotal(0);
      setHasMore(false);
    } finally {
      setLoading(false);
      setHasFetched(true);
    }
  }, [storesReady, fetchProductsPage]);

  const loadMore = useCallback(async () => {
    if (!storesReady || loading || loadingMore || !hasMore || loadMoreInFlightRef.current) {
      return;
    }

    const pageNum = nextPageRef.current;
    loadMoreInFlightRef.current = true;
    setLoadingMore(true);
    try {
      const result = await fetchProductsPage(pageNum);
      setProducts((prev) => {
        const seen = new Set(prev.map((p) => p.id));
        const added = result.products.filter((p) => !seen.has(p.id));
        return [...prev, ...added];
      });
      setTotal(result.total);
      setHasMore(result.currentPage < result.totalPages);
      nextPageRef.current = pageNum + 1;
    } catch (err) {
      showApiError(err, "Failed to load more products");
    } finally {
      loadMoreInFlightRef.current = false;
      setLoadingMore(false);
    }
  }, [storesReady, loading, loadingMore, hasMore, fetchProductsPage, showApiError]);

  loadMoreRef.current = loadMore;

  useEffect(() => {
    const root = scrollRef.current;
    const sentinel = loadMoreSentinelRef.current;
    if (!root || !sentinel || !hasMore || loading || loadingMore) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) loadMoreRef.current();
      },
      { root, rootMargin: "40px", threshold: 0 }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loading, loadingMore]);

  useEffect(() => {
    loadStores();
  }, [loadStores]);

  useEffect(() => {
    loadInitial();
  }, [loadInitial]);

  const handleToggleStatus = async (p) => {
    setTogglingId(p.id);
    const nextStatus = !p.status;
    try {
      await api.put(`/product/manage/status/${p.id}`, { status: nextStatus });
      setProducts((prev) =>
        prev.map((r) => r.id === p.id ? { ...r, status: nextStatus } : r)
      );
    } catch (err) {
      showApiError(err, "Failed to update status");
    } finally {
      setTogglingId(null);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/product/delete/${delDialog.id}`);
      setDelDialog({ open: false, id: null, name: "" });
      loadInitial();
    } catch (err) {
      showApiError(err, "Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  const getStoreName = () => stores.find(s => s.store_id === selectedStore)?.title_en || "Selected Store";
  const isStoreInactive = stores.find(s => s.store_id === selectedStore)?.status === false;

  const COLS = [
    { label: "", align: "center" },
    { label: "#", align: "center" },
    { label: "Image", align: "left" },
    { label: "Name", align: "left" },
    { label: "Description", align: "left" },
    { label: "Category", align: "left" },
    { label: "Price", align: "right" },
    { label: "Variants", align: "center" },
    { label: "Highlight", align: "center" },
    { label: "Seq", align: "center" },
    { label: "Max Count", align: "center" },
    { label: "Status", align: "center" },
    { label: "Actions", align: "center" },
  ];

  return (
    <Box sx={{ p: 1 }}>
      <PageHeader
        title="Master Products"
        subtitle={
          selectedStore
            ? `${getStoreName()} — manage products for the selected store.`
            : "Select a store to view and manage its product catalog."
        }
        count={total}
        titleExtra={
          selectedStore && isStoreInactive ? (
            <Chip label="Store Inactive" size="small" color="error" variant="outlined" sx={{ fontWeight: 600 }} />
          ) : null
        }
        actionLabel="Add Product"
        actionIcon={AddIcon}
        onAction={() => setFormModal({ open: true, id: null })}
        actionDisabled={!selectedStore}
      />

      {/* Control Bar */}
      <MainCard sx={{ mb: 3, border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.04)' }} content={false}>
        <Box sx={{ p: 2.5, display: "flex", gap: 3, alignItems: "center", flexWrap: "wrap" }}>
          <FormControl size="small" sx={{ minWidth: 260 }}>
            <InputLabel id="store-select-label">Filter by Active Store</InputLabel>
            <Select
              labelId="store-select-label"
              value={selectedStore}
              label="Filter by Active Store"
              onChange={(e) => setSelectedStore(e.target.value)}
              startAdornment={
                <InputAdornment position="start">
                  <ShopIcon fontSize="small" color="action" />
                </InputAdornment>
              }
              sx={{ borderRadius: 2 }}
            >
              <MenuItem value=""><em>All Global Stores</em></MenuItem>
              {stores.map((s) => (
                <MenuItem key={s.store_id} value={s.store_id}>
                  {s.title_en}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 220 }} disabled={!selectedStore}>
            <InputLabel id="category-select-label">Category</InputLabel>
            <Select
              labelId="category-select-label"
              value={selectedCategory}
              label="Category"
              displayEmpty
              onChange={(e) => setSelectedCategory(e.target.value)}
              renderValue={(value) => {
                if (!value || value === ALL_CATEGORIES) return "All category";
                const cat = categories.find((c) => String(c.id) === String(value));
                return cat?.name_en || cat?.name_ar || "All category";
              }}
              startAdornment={
                <InputAdornment position="start">
                  <CategoryIcon fontSize="small" color="action" />
                </InputAdornment>
              }
              sx={{ borderRadius: 2 }}
            >
              <MenuItem value={ALL_CATEGORIES}>All category</MenuItem>
              {categories.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name_en || c.name_ar || "—"}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            size="small"
            placeholder="Search by name, description…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
              sx: { borderRadius: 2 }
            }}
            sx={{ maxWidth: 450, flex: 1 }}
          />
          
          <Tooltip title="Synchronize Inventory">
            <IconButton 
              onClick={() => { loadStores(); loadInitial(); }} 
              sx={{ 
                bgcolor: 'grey.50', 
                border: '1px solid', 
                borderColor: 'divider',
                borderRadius: 2
              }}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </MainCard>

      {/* Table Data */}
      <MainCard contentSX={{ p: 0 }} sx={{ border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.04)', borderRadius: 4, overflow: 'hidden' }}>
        <TableContainer
          ref={scrollRef}
          component={Paper}
          elevation={0}
          sx={{
            display: "block",
            maxHeight: { xs: "55vh", md: 520 },
            minHeight: 360,
            overflowX: "auto",
            overflowY: "scroll",
            scrollbarWidth: "thin",
            scrollbarColor: "rgba(31, 91, 64, 0.45) rgba(31, 91, 64, 0.08)",
            WebkitOverflowScrolling: "touch",
            "&::-webkit-scrollbar": { width: 10, height: 10 },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "rgba(31, 91, 64, 0.4)",
              borderRadius: 8,
              border: "2px solid rgba(31, 91, 64, 0.06)"
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "rgba(31, 91, 64, 0.08)",
              borderRadius: 8
            }
          }}
        >
          <Table stickyHeader sx={{ minWidth: 1300 }}>
            <TableHead>
              <TableRow>
                {COLS.map((col, i) => (
                  <TableCell
                    key={i}
                    align={col.align}
                    sx={{
                      fontWeight: 700,
                      fontSize: "0.72rem",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      bgcolor: "grey.50",
                      whiteSpace: "nowrap",
                      borderBottom: "2px solid",
                      borderColor: "divider",
                      py: 1.5
                    }}
                  >
                    {col.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading || !storesReady ? (
                <TableRow>
                  <TableCell colSpan={COLS.length} align="center" sx={{ py: 10 }}>
                    <CircularProgress size={36} />
                  </TableCell>
                </TableRow>
              ) : products.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={COLS.length} align="center" sx={{ py: 10 }}>
                    <Typography variant="h6" fontWeight={600} color="text.secondary">
                      {hasFetched ? "No Products Found" : "Loading products…"}
                    </Typography>
                    {hasFetched && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        No inventory matches your current filters.
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              ) : (
                products.map((p, idx) => (
                  <ProductRow
                    key={p.id}
                    p={p}
                    idx={idx}
                    onEdit={(prod) => setFormModal({ open: true, id: prod.id })}
                    onDelete={(prod) =>
                      setDelDialog({ open: true, id: prod.id, name: prod.name })
                    }
                    onToggleStatus={handleToggleStatus}
                    togglingId={togglingId}
                  />
                ))
              )}
              {!loading && storesReady && products.length > 0 && (
                <TableRow>
                  <TableCell
                    ref={loadMoreSentinelRef}
                    colSpan={COLS.length}
                    align="center"
                    sx={{ py: 1.5, border: 0 }}
                  >
                    {loadingMore ? (
                      <CircularProgress size={24} />
                    ) : !hasMore ? (
                      <Typography variant="caption" color="text.secondary">
                        All {total} products loaded
                      </Typography>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        Scroll for more
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </MainCard>

      {/* Product Form Modal */}
      <ProductFilteredByStoreForm 
        open={formModal.open}
        productId={formModal.id}
        storeId={selectedStore}
        storeName={getStoreName()}
        initialCategoryId={
          formModal.id
            ? products.find((p) => p.id === formModal.id)?.category_id
            : selectedCategory !== ALL_CATEGORIES ? selectedCategory : undefined
        }
        onSave={loadInitial}
        onClose={() => setFormModal({ open: false, id: null })}
      />

      {/* Delete Confirmation */}
      <Dialog
        open={delDialog.open}
        onClose={() => !deleting && setDelDialog({ open: false, id: null, name: "" })}
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Withdraw Product</DialogTitle>
        <DialogContent>
          <Typography>
            Are you certain you want to withdraw <strong>{delDialog.name}</strong> from the inventory? This will remove it from all storefronts immediately.
          </Typography>
        </DialogContent>
        <Box sx={{ p: 3, pt: 0, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button variant="outlined" color="inherit" onClick={() => setDelDialog({ open: false, id: null, name: "" })} disabled={deleting}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            color="error" 
            onClick={handleDelete} 
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
          >
            {deleting ? "Withdrawing..." : "Confirm Withdrawal"}
          </Button>
        </Box>
      </Dialog>
    </Box>
  );
}
